/**
 * trailers (DEC-011 / master spec §86).
 *
 * The ASSET side of the YARDS-TRUCKS-TRAILERS triad. Trailers are tracked
 * separately from trucks because a trailer may be detached from any truck
 * and parked at a yard, OR pulled by a different truck on a different
 * job — many-to-one over time. Each trailer has a current_yard_id (where
 * it is right now) and a home_yard_id (where it returns to) per master
 * spec §86 verbatim.
 *
 * Tenant-scoped (RLS via standard predicate). Hauler-only enforcement is
 * Phase 3 role-guard's job (no schema-level CHECK; documented in DEC-011 /
 * D-19). Holds master spec §86 columns verbatim.
 *
 * trailer_type CHECK enum (master spec §86 verbatim — locked):
 *   VAC_TRAILER / FLATBED / BELLY_DUMP / END_DUMP / MUD_TANK /
 *   FRAC_TANK / OTHER
 *
 * FK posture:
 *   - tenant_id → tenants.id (cascade): inline.
 *   - current_yard_id → yards.id (set null): inline (yards.ts ships in
 *     the same plan, before trailers.ts in import order).
 *   - home_yard_id → yards.id (set null): inline.
 *   - assigned_truck_id → trucks.id (set null): inline (Plan 02-04 wired
 *     this FK once both Plans 02-02 and 02-03 had landed). drivers.ts
 *     declares `trucks` and is imported here from `./drivers.js`; no
 *     circular import risk because drivers.ts does NOT import trailers.ts.
 *
 * Indexes (master spec §86 explicit — 4 total):
 *   - (tenant_id) — tenant's trailer list.
 *   - (current_yard_id) — yard's current-trailer list.
 *   - (assigned_truck_id) — truck's currently-pulled trailer.
 *   - (home_yard_id) — yard's home-trailer list.
 *
 * NEGATIVE NOTE — `multi_yard_dispatch` is NOT a column anywhere
 * (DEC-011 sweep). Trailer-to-yard movement is recorded directly on this
 * table; dispatcher visibility into trailers is governed by the parent
 * tenant's RLS scope, not by a per-yard feature flag.
 *
 * Sources:
 *   - master spec §86 (YARDS, TRUCKS, AND TRAILERS — DEC-011 verbatim spec)
 *   - master spec §10 (Truck selection at shift start; informs trailer-truck
 *     pairing semantics)
 *   - DEC-011 (Yards — Core Platform Feature; user-authored)
 *   - CONTEXT D-19 (Phase 2 schema addendum recording DEC-011)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - trailer_type is FREE-TEXT (Quick 260616-flt / DEC-067 dropped the original
 *     master spec §86 7-value CHECK via migration 0030 for the Fleet UI).
 *   - 4 indexes from §86 are present.
 *   - assigned_truck_id FK to trucks.id is wired inline (Plan 02-04 finalized).
 */

import {
  boolean,
  foreignKey,
  index,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';
import { yards } from './yards.js';
import { trucks } from './drivers.js';
import { importBatches } from './imports.js';

/**
 * trailers — DEC-011 / master spec §86.
 *
 * 4 indexes per §86 verbatim. trailer_type allowlist enforced via typed
 * check(). assigned_truck_id FK constraint deferred to Plan 02-04
 * relations.ts (cross-plan FK to drivers.ts trucks; parallel sibling).
 */
export const trailers = pgTable(
  'trailers',
  {
    id: uuid('trailer_id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Hauler-internal asset number, e.g. "TR-014".
    unitNumber: varchar('unit_number').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    trailerType: text('trailer_type').notNull(),
    // Tank/load capacity in barrels — nullable per master spec §86 (some
    // trailer types have no BBL capacity, e.g. FLATBED).
    capacityBbl: numeric('capacity_bbl', { precision: 10, scale: 2 }),
    // DEC-011 / master spec §86 — current location.
    // NULL = on a job or location unknown.
    currentYardId: uuid('current_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    // DEC-011 / master spec §86 — default yard the trailer returns to.
    // NULL = no default home yard assigned.
    homeYardId: uuid('home_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    // FK wired inline in Plan 02-04 once drivers.ts trucks existed.
    // NULL = not currently assigned to a truck.
    assignedTruckId: uuid('assigned_truck_id').references(() => trucks.id, {
      onDelete: 'set null',
    }),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('trailers_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'trailers_import_batch_tenant_fk',
    }).onDelete('set null'),
    // Master spec §86 explicit indexes — 4 total.
    index('trailers_tenant_idx').on(t.tenantId),
    index('trailers_current_yard_idx').on(t.currentYardId),
    index('trailers_assigned_truck_idx').on(t.assignedTruckId),
    index('trailers_home_yard_idx').on(t.homeYardId),
    // Quick 260616-flt / DEC-067 — trailer_type is now FREE-TEXT. The original
    // master spec §86 7-value CHECK was dropped (migration 0030) so the Fleet UI
    // can capture any trailer type the hauler uses; the column stays text NOT NULL.
    pgPolicy('trailers_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Trailer = typeof trailers.$inferSelect;
export type NewTrailer = typeof trailers.$inferInsert;
export const trailerSelectSchema = createSelectSchema(trailers);
export const trailerInsertSchema = createInsertSchema(trailers);
